import * as GeoTIFF from 'geotiff/dist-browser/geotiff'
import * as proj4 from 'proj4'
import type { Extent } from 'ol/extent'
import type { WcsRgbLayer } from '../state'

const DEFAULT_GEOSERVER = 'https://landscapes.wearepal.ai/geoserver/'
const DEFAULT_SCALE_FACTOR = 0.35
const DEFAULT_STRETCH_MIN = 0
/** Fallback when the raster has no usable nonzero samples for percentile stretch. */
const DEFAULT_STRETCH_MAX = 3500

type Proj4Module = {
  (from: string, to: string, coord: [number, number]): [number, number]
  defs: (epsg: string, def?: string) => unknown
}

function getProj4(): Proj4Module {
  return (proj4 as any).default as Proj4Module
}

function ensureCrs(epsg: string): void {
  const proj = getProj4()
  if (typeof proj?.defs !== 'function' || proj.defs(epsg)) {
    return
  }
  const match = /^EPSG:(326|327)(\d{2})$/.exec(epsg)
  if (!match) {
    return
  }
  const zone = Number(match[2])
  const south = match[1] === '327'
  proj.defs(
    epsg,
    `+proj=utm +zone=${zone}${south ? ' +south' : ''} +datum=WGS84 +units=m +no_defs +type=crs`
  )
}

function extentTo3857(bbox: [number, number, number, number], fromEpsg: string): Extent {
  ensureCrs(fromEpsg)
  const [minX, minY, maxX, maxY] = bbox
  const samples = 9
  const points: Array<[number, number]> = []
  for (let i = 0; i <= samples; i++) {
    const t = i / samples
    points.push([minX + (maxX - minX) * t, minY])
    points.push([minX + (maxX - minX) * t, maxY])
    points.push([minX, minY + (maxY - minY) * t])
    points.push([maxX, minY + (maxY - minY) * t])
  }
  const proj = getProj4()
  const projected = points.map(p => proj(fromEpsg, 'EPSG:3857', p))
  const xs = projected.map(p => p[0]).filter(Number.isFinite)
  const ys = projected.map(p => p[1]).filter(Number.isFinite)
  if (xs.length === 0 || ys.length === 0) {
    throw new Error(`Could not reproject WCS extent from ${fromEpsg} to EPSG:3857.`)
  }
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)]
}

function coverageIdForWcs2(coverageId: string): string {
  return coverageId.includes('__') ? coverageId : coverageId.replace(':', '__')
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return Number.NaN
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor((sorted.length - 1) * p)))
  return sorted[idx]
}

function stretchValue(value: number, min: number, max: number): number {
  if (!(max > min)) return 0
  const t = (value - min) / (max - min)
  if (t <= 0) return 0
  if (t >= 1) return 255
  return Math.round(t * 255)
}

export type WcsRgbOverlayData = {
  extent3857: Extent
  imageDataUrl: string
}

/** Fetch a GeoServer coverage via WCS 2.0.1 and build an RGB data-URL overlay. */
export async function loadWcsRgbOverlay(layer: WcsRgbLayer): Promise<WcsRgbOverlayData> {
  const base = layer.externalLink ?? DEFAULT_GEOSERVER
  const nativeCrs = layer.nativeCrs ?? 'EPSG:32651'
  const [minX, minY, maxX, maxY] = layer.nativeExtent
  const scaleFactor = layer.scaleFactor ?? DEFAULT_SCALE_FACTOR
  const rgbBands = layer.rgbBands ?? [1, 2, 3]

  const axis = nativeCrs === 'EPSG:4326' ? (['Long', 'Lat'] as const) : (['E', 'N'] as const)
  const params = new URLSearchParams({
    service: 'WCS',
    version: '2.0.1',
    request: 'GetCoverage',
    coverageId: coverageIdForWcs2(layer.coverageId),
    format: 'image/tiff',
  })
  // URLSearchParams cannot repeat keys; append subset/scalefactor manually.
  let url =
    `${base.replace(/\/?$/, '/') }wcs?${params.toString()}` +
    `&subset=${axis[0]}(${minX},${maxX})` +
    `&subset=${axis[1]}(${minY},${maxY})`
  if (scaleFactor > 0 && scaleFactor < 1) {
    url += `&scalefactor=${scaleFactor}`
  }

  const response = await fetch(url)
  const arrayBuffer = await response.arrayBuffer()
  const head = new TextDecoder().decode(arrayBuffer.slice(0, 64))
  if (!response.ok || head.startsWith('<?xml') || head.startsWith('<ows:') || head.startsWith('<ServiceException')) {
    throw new Error(
      `WCS GetCoverage failed for ${layer.coverageId} (${response.status}): ${head.slice(0, 240)}`
    )
  }

  const tiff = await GeoTIFF.fromArrayBuffer(arrayBuffer)
  const image = await tiff.getImage()
  const width = image.getWidth()
  const height = image.getHeight()
  const rasters = (await image.readRasters({
    interleave: false,
    samples: rgbBands.map(b => b - 1),
  })) as Array<ArrayLike<number>>

  if (rasters.length < 3) {
    throw new Error(`Expected 3 bands from WCS coverage ${layer.coverageId}, got ${rasters.length}.`)
  }

  const [red, green, blue] = rasters
  const pixels = width * height
  const sample: number[] = []
  const sampleStride = Math.max(1, Math.floor(pixels / 20000))
  for (let i = 0; i < pixels; i += sampleStride) {
    const v = Number(red[i])
    if (v > 0) sample.push(v)
  }
  sample.sort((a, b) => a - b)

  const stretchMin = layer.stretchMin ?? DEFAULT_STRETCH_MIN
  const stretchMax =
    layer.stretchMax ??
    (sample.length > 0 ? Math.max(percentile(sample, 0.98), stretchMin + 1) : DEFAULT_STRETCH_MAX)

  const rgba = new Uint8ClampedArray(pixels * 4)
  for (let i = 0, j = 0; i < pixels; i++, j += 4) {
    const r = Number(red[i])
    const g = Number(green[i])
    const b = Number(blue[i])
    const empty = r === 0 && g === 0 && b === 0
    rgba[j] = stretchValue(r, stretchMin, stretchMax)
    rgba[j + 1] = stretchValue(g, stretchMin, stretchMax)
    rgba[j + 2] = stretchValue(b, stretchMin, stretchMax)
    rgba[j + 3] = empty ? 0 : 255
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Could not get a 2d canvas context for WCS RGB preview.')
  }
  ctx.putImageData(new ImageData(rgba, width, height), 0, 0)

  return {
    extent3857: extentTo3857(layer.nativeExtent, nativeCrs),
    imageDataUrl: canvas.toDataURL('image/png'),
  }
}
